import { inject, Injectable, Injector } from '@angular/core';
import { Directionality } from '@angular/cdk/bidi';
import { ComponentType } from '@angular/cdk/portal';
import { MatDialog, MatDialogConfig, type DialogPosition } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { BzMessageDialog, BzMessageDialogData } from './bz-message-dialog';
import { BzPromptDialog, BzPromptDialogData } from './bz-prompt-dialog';
import { BZ_DIALOG_DATA, BZ_DIALOG_RESIZABLE, BzDialogRef } from './bz-dialog-ref';

/**
 * `center` is a modal. `start` and `end` dock it to the edge of the viewport
 * as a full-height sheet — logical rather than left/right, because the
 * console runs in Hebrew as well and a panel that is always on the right is
 * wrong half the time.
 */
export type BzDialogPosition = 'center' | 'start' | 'end';

export interface BzDialogOptions<D = unknown> {
  /** Passed to the dialog body, which reads it through `BZ_DIALOG_DATA`. */
  data?: D;
  width?: string;
  maxWidth?: string;
  height?: string;
  /** Backdrop click and Escape close by default; false forces a choice. */
  closable?: boolean;
  /** Accessible name when the body has no visible heading. */
  ariaLabel?: string;
  /** Extra class on the overlay pane, for a one-off size or layout. */
  panelClass?: string;
  /**
   * Docks the dialog to a viewport edge as a full-height sheet instead of
   * centring it.
   *
   * This is what the console's right-hand edit panels are — the shift panel,
   * the services panel and the rest are `md-sidenav`s holding a coloured
   * header, a scrolling body of cards and a sticky footer, which is
   * `bz-dialog-layout` with a different position and nothing else. Nine of
   * the console's 57 `md-sidenav`s are this shape.
   *
   * The other 48 use `md-is-locked-open`: they sit *beside* the content as
   * part of the layout rather than over it, and those are not dialogs. A
   * sheet has a backdrop, traps focus and closes on Escape; a docked panel
   * does none of those.
   */
  position?: BzDialogPosition;
  /**
   * Lets the user resize the dialog by dragging its bottom-right corner.
   *
   * Off by default: a canned confirm has nothing worth resizing, and the
   * handle would be noise. Worth turning on for dialogs holding a table, a
   * map or a long form — the shapes where the caller cannot know in advance
   * how much room the user wants.
   */
  resizable?: boolean;
}

/**
 * Opens dialogs.
 *
 * `open()` is the general case and the one that matters: it puts **any**
 * component on screen and resolves with whatever that component closes with.
 * The console has 22 bespoke dialogs today — filters, file upload, colour and
 * location pickers, metadata editors — and every one is
 * `$mdDialog.show({ template, controller })` returning a promise. That is the
 * shape `open()` replaces.
 *
 * `confirm()`, `alert()` and `prompt()` sit on top for the three shapes common
 * enough to be worth canning. They exist so a yes/no question is one line
 * instead of a component.
 *
 * A dialog body written for `open()` injects `BZ_DIALOG_DATA` and
 * `BzDialogRef`, never Material — see `bz-dialog-ref.ts`. Use
 * `bz-dialog-layout` for the header/body/footer chrome.
 *
 * Everything returns a promise rather than an observable. A dialog resolves
 * exactly once, every call site awaits it, and it matches how the console's
 * existing `commonsDialogService` is already used — so migrating a call site
 * stays a rename.
 */
@Injectable({ providedIn: 'root' })
export class BzDialogService {
  private readonly dialog = inject(MatDialog);
  private readonly injector = inject(Injector);
  private readonly direction = inject(Directionality);

  /**
   * Opens any component as a dialog. Resolves with the value the body closed
   * with, or `undefined` if it was dismissed with Escape or a backdrop click.
   */
  async open<C, R = unknown, D = unknown>(
    component: ComponentType<C>,
    options: BzDialogOptions<D> = {},
  ): Promise<R | undefined> {
    // A custom injector, not `config.providers` — MatDialogConfig has no such
    // field, and casting one in silently does nothing.
    const bzRef = new BzDialogRef<R>();
    const injector = Injector.create({
      providers: [
        { provide: BZ_DIALOG_DATA, useValue: options.data },
        { provide: BZ_DIALOG_RESIZABLE, useValue: options.resizable === true },
        { provide: BzDialogRef, useValue: bzRef },
      ],
      parent: this.injector,
    });

    const ref = this.dialog.open<C, D, R>(component, {
      ...this.baseConfig(options),
      injector,
    });

    // Synchronous: open() has already created the body, and nothing can have
    // interacted with it yet. See the note on BzDialogRef.
    bzRef.attach(ref);

    return await firstValueFrom(ref.afterClosed());
  }

  /** Yes/no question. Resolves false when cancelled or dismissed. */
  async confirm(data: BzMessageDialogData, options: BzDialogOptions = {}): Promise<boolean> {
    const result = await this.open<BzMessageDialog, boolean, BzMessageDialogData>(BzMessageDialog, {
      ...options,
      data: { ...data, mode: 'confirm' },
    });
    // Dismissing yields undefined, which must read as "not confirmed" rather
    // than as a truthy value.
    return result === true;
  }

  /** Single-button message. Resolves when acknowledged or dismissed. */
  async alert(
    data: Omit<BzMessageDialogData, 'cancelLabel' | 'mode'>,
    options: BzDialogOptions = {},
  ): Promise<void> {
    await this.open<BzMessageDialog, boolean, BzMessageDialogData>(BzMessageDialog, {
      ...options,
      data: { ...data, mode: 'alert' },
    });
  }

  /**
   * Asks for one string. Resolves the value, or `null` when cancelled —
   * distinct from `''`, which is a value the user actually chose.
   */
  async prompt(data: BzPromptDialogData, options: BzDialogOptions = {}): Promise<string | null> {
    const result = await this.open<BzPromptDialog, string | null, BzPromptDialogData>(
      BzPromptDialog,
      { ...options, data },
    );
    return result ?? null;
  }

  private baseConfig<D>(options: BzDialogOptions<D>): MatDialogConfig<D> {
    const sheet = options.position !== undefined && options.position !== 'center';

    /*
     * An array, not a joined string.
     *
     * `panelClass` reaches `classList.add()`, which throws
     * InvalidCharacterError on a single token containing spaces — so a
     * space-joined string works only while exactly one class is present and
     * throws the moment a second one appears. It threw for the first time on
     * `position`, which always contributes two, but the bug was already here:
     * `panelClass: 'mine'` together with `resizable: true` produced
     * "mine bz-dialog-resizable" and would have thrown just the same.
     *
     * Worth knowing how it failed, because it is not obvious from a stack
     * trace: the exception aborts `MatDialog.open` partway, so the pane keeps
     * the width and height that were already applied and silently loses its
     * classes, its position and its body. It looks like a styling problem.
     */
    const panelClass = [
      options.panelClass,
      options.resizable ? 'bz-dialog-resizable' : '',
      sheet ? 'bz-dialog-sheet' : '',
      sheet ? `bz-dialog-sheet--${options.position}` : '',
    ].filter((name): name is string => !!name);

    return {
      // A sheet is a form panel rather than a question, so it starts wider.
      // The console's shift panel renders at 700px; 520 is the narrower
      // default a caller can widen, rather than a width most sheets fight.
      width: options.width ?? (sheet ? '520px' : '420px'),
      // CSS `resize` writes inline width/height, and an inline max-width from
      // here would clamp it — the dialog would refuse to grow past 92vw and
      // look broken rather than capped. So a resizable dialog gives up the cap
      // and lets the user's own drag be the limit.
      maxWidth: options.maxWidth ?? (options.resizable ? 'none' : '92vw'),
      // Full height, because a sheet docked to an edge that stops short of the
      // viewport reads as a misplaced modal rather than as a panel.
      height: options.height ?? (sheet ? '100vh' : undefined),
      position: this.resolvePosition(options.position),
      disableClose: options.closable === false,
      ariaLabel: options.ariaLabel,
      panelClass: panelClass.length > 0 ? panelClass : undefined,
      autoFocus: 'first-tabbable',
      restoreFocus: true,
    };
  }

  /**
   * Logical position to the physical one Material speaks.
   *
   * `MatDialogConfig.position` only takes `top`/`bottom`/`left`/`right` —
   * `updatePosition` calls `strategy.left()` or `strategy.right()` directly —
   * so `start`/`end` have to be resolved against the document direction here.
   * Getting this wrong is invisible in English and puts every panel on the
   * wrong edge in Hebrew, which is half of what this product ships.
   *
   * `top: '0'` as well, because with only a horizontal edge set Material
   * centres the dialog vertically and a half-height sheet is not a sheet.
   */
  private resolvePosition(position: BzDialogPosition | undefined): DialogPosition | undefined {
    if (position === undefined || position === 'center') return undefined;

    const rtl = this.direction.value === 'rtl';
    const atStart = position === 'start';
    // start is left in LTR and right in RTL; end is the mirror of that.
    return atStart === rtl ? { top: '0', right: '0' } : { top: '0', left: '0' };
  }
}
